import firebase from 'firebase';

interface NotificationActivity {
  id: string;
  date: Date;
  title: string;
  contentType: 'Email' | 'Notification';
  notificationType?: 'Web' | 'Mobile App';
  deliveryState: string | null;
  status: string;
  statusSource?: 'Firebase' | 'SendGrid' | 'Unknown';
  sendgridMessageId?: string;
  sendgridMessageIdFull?: string;
  rawData: Record<string, unknown>;
}

interface ReadByStaff {
  id: string;
  dateTime: firebase.firestore.Timestamp;
}

const getNotificationActivity = async (
  userId: string,
  userEmail: string,
  facilityId: string
): Promise<{ items: NotificationActivity[] }> => {
  const items: NotificationActivity[] = [];

  try {
    // Fetch ALL emails from last 30 days (aligns with SendGrid Activity Feed retention)
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - 30);
    const emailThreshold = firebase.firestore.Timestamp.fromDate(daysAgo);

    const emailQuery = firebase
      .firestore()
      .collection('email')
      .where('to', '==', userEmail)
      .where('facilityId', '==', facilityId)
      .where('delivery.startTime', '>=', emailThreshold)
      .orderBy('delivery.startTime', 'desc');
    
    const emailsSnapshot = await emailQuery.get();
    const allEmailDocs = emailsSnapshot.docs;

    allEmailDocs.forEach(doc => {
      const data = doc.data();
      const delivery = data.delivery || {};

      let status = 'Delivered';
      let statusSource: 'Firebase' | 'SendGrid' | 'Unknown' = 'Unknown';
      
      // Check if SendGrid webhook data is available
      const hasSendGridTracking = data.sendgridMessageId && data.lastEventType;
      const sentViaSendGrid = data.status === 'sent' || data.sendgridMessageId;
      
      // Prioritize SendGrid webhook data (more accurate)
      if (data.spamReported) {
        status = 'Spam Report';
        statusSource = 'SendGrid';
      } else if (data.bounced) {
        status = 'Bounced';
        statusSource = 'SendGrid';
      } else if (data.dropped) {
        status = 'Dropped';
        statusSource = 'SendGrid';
      } else if (data.unsubscribed) {
        status = 'Unsubscribed';
        statusSource = 'SendGrid';
      } else if (data.groupUnsubscribed) {
        status = 'Group Unsubscribe';
        statusSource = 'SendGrid';
      } else if (data.groupResubscribed) {
        status = 'Group Resubscribe';
        statusSource = 'SendGrid';
      } else if (data.clicked) {
        const openCount = data.openCount || 0;
        const clickCount = data.clickCount || 0;
        const clickedUrls = data.clickedUrls || [];
        status = `Delivered, Opened (${openCount}) and Clicked (${clickCount})`;
        if (clickedUrls.length > 0) {
          status += ` - ${clickedUrls.join(', ')}`;
        }
        statusSource = 'SendGrid';
      } else if (data.opened) {
        const openCount = data.openCount || 0;
        status = `Delivered and Opened (${openCount})`;
        statusSource = 'SendGrid';
      } else if (data.delivered || data.lastEventType === 'delivered') {
        status = 'Delivered';
        statusSource = 'SendGrid';
      } else if (delivery.state === 'SUCCESS') {
        status = 'Sent to SendGrid';
        statusSource = 'Firebase';
        // If sent via SendGrid but no webhook data yet
        if (sentViaSendGrid && !hasSendGridTracking) {
          status = 'Sent - awaiting delivery confirmation';
        }
      } else if (delivery.state === 'ERROR') {
        status = 'Failed to Send';
        statusSource = 'Firebase';
      } else if (delivery.state === 'PENDING') {
        status = 'Pending';
        statusSource = 'Firebase';
      } else if (sentViaSendGrid && !hasSendGridTracking) {
        // Sent via SendGrid but no data from either system yet
        status = 'Sent - awaiting confirmation';
        statusSource = 'Unknown';
      }

      const emailItem: NotificationActivity = {
        id: doc.id,
        date: delivery.startTime?.toDate() || delivery.endTime?.toDate() || data.sentAt?.toDate() || new Date(),
        title: data.message?.subject || data.subject || 'No Subject',
        contentType: 'Email',
        deliveryState: delivery.state || (statusSource === 'SendGrid' ? data.lastEventType : null) || 'UNKNOWN',
        status: status,
        statusSource: statusSource,
        sendgridMessageId: data.sendgridMessageId || undefined,
        sendgridMessageIdFull: data.sendgridMessageIdFull || undefined,
        rawData: data
      };

      items.push(emailItem);
    });

    // Fetch notifications where this user is in staffUserIds (tracked notifications)
    const notificationQuery = firebase
      .firestore()
      .collection('notifications')
      .where('to', '==', 'staff')
      .where('staffUserIds', 'array-contains', userId)
      .where('facilityId', '==', facilityId)
      .orderBy('dateTimeCreated', 'desc');

    const notificationsSnapshot = await notificationQuery.get();
    const allNotificationDocs = notificationsSnapshot.docs;

    allNotificationDocs.forEach(doc => {
      const data = doc.data();

      // Check if this staff member has read this notification
      const readByStaffs = (data.readByStaffs || []) as ReadByStaff[];
      const hasRead = Array.isArray(readByStaffs) && readByStaffs.some((staff: ReadByStaff) => staff.id === userId);
      const isRead = hasRead ? 'Read' : 'Unread';

      const notificationItem: NotificationActivity = {
        id: doc.id,
        date: data.dateTimeCreated?.toDate() || new Date(),
        title: data.title || 'No Title',
        contentType: 'Notification',
        notificationType: data.staffAppNotification === true ? 'Mobile App' : 'Web',
        deliveryState: null,
        status: isRead,
        rawData: data
      };

      items.push(notificationItem);
    });

    // Sort all items by date descending
    items.sort((a, b) => b.date.getTime() - a.date.getTime());

    return { items };
  } catch (error) {
    console.error('Error fetching notification activity:', error);
    throw error;
  }
};

export const staff = {
  getNotificationActivity
};
